import {
  AssetChatFeedItem,
  AssetChatSideChat,
  formatShortDateTimeOrSameDay,
  IssueType,
  stripFormattingMarkers,
  replaceMentionPlaceholderWithNames,
} from '@gripp/shared-logic';
import { createElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type GestureResponderEvent,
  Platform,
  Pressable,
  Text,
  View,
} from 'react-native';
import { Avatar } from '../crop-circle/Avatar';
import { Icon } from '../crop-circle/icons';
import { Pill } from '../crop-circle/Components/Pill';
import { severityToVariant, TypeIcon } from '../crop-circle/Components/Type';
import { colors, spacing, tokens } from '../crop-circle/tokens';
import { renderFormattedMessageText } from '../issue/formattedMessageText';
import { AnonymousId, FunctionId, SystemName } from '../issue/issueFormatUtils';
import { MessageActionMenu } from '../issue/messageActionMenu';
import { RenderMediaItem } from '../media/renderMediaItem';
import { useTypedMedia } from '../media/useTypedMedia';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type AssetChatRowProps = {
  item: AssetChatFeedItem;
  /** Compared against the row's author id to render the author as "You". */
  currentAccountId?: string;
  /**
   * Opens the row's Issue thread. Wired to the whole row (both shapes), not
   * just the "N replies" pill — most rows carry no replies yet and the pill
   * doesn't render for them, so the row itself is the only way in.
   *
   * Omitted where the row is not a feed row: a side chat's source row reuses
   * this presentation so the reader sees the row they just came from, but it
   * is already at its destination, so it renders inert and without a menu.
   */
  onOpenThread?: () => void;
  /** Reuses the activity list's unread decoration (a dot beside the timestamp). */
  isUnread?: boolean;
  /**
   * Every side chat anywhere in this row's Issue thread that the viewer can
   * see — thread-wide, not just the one on its origin message, because the row
   * is meant to be an index of its own conversation. Server-filtered; see
   * `useAssetChatSideChats`.
   */
  sideChats?: AssetChatSideChat[];
  /** Opens one of those side chats. */
  onOpenSideChat?: (parentMessageId: string) => void;
  /** Starts a side chat on this row's origin message. */
  onStartSideChat?: () => void;
  /** Rings this row's author 1:1. Absent when they are not callable. */
  onCall?: () => void;
  /** Copies the row's body text. Absent on a compact line, which has none. */
  onCopy?: () => void;
  /** Opens the share panel for this activity. Web only. */
  onShare?: () => void;
  testID?: string;
};

type ChatActor = AssetChatFeedItem['createdBy'];

/**
 * 1-2 letter initials for an author's initials avatar. Exported so other
 * surfaces sharing the same chip (the Participants band) derive initials the
 * same way rather than re-deriving the rule.
 */
export const getInitials = (name: string): string => {
  const initials = name
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return initials || '?';
};

/**
 * Same Anonymous/Function-id normalization `getIssueFullName` applies, over
 * the feed's own lightweight actor shape (`name.first` is optional here,
 * unlike the `Name` class `getFullName` expects, so it isn't reused).
 */
const getActorName = (actor: ChatActor, unknownLabel: string): string => {
  if (!actor?.id) return unknownLabel;
  if (actor.id === AnonymousId) return unknownLabel;
  if (actor.id === FunctionId) return SystemName;
  const fullName = [actor.name?.first, actor.name?.last]
    .filter(Boolean)
    .join(' ');
  return fullName || unknownLabel;
};

/**
 * One row in the asset chat feed — **one row type** for every `IssueType`:
 * an author header (initials avatar, name, a severity/type glyph, timestamp —
 * no organization label, which is a separate piece of work) and, beneath it,
 * what the person wrote. No card, no asset-name title, no asset thumbnail —
 * the reader is already inside this asset, so all three would repeat context
 * on every row, which is what made the first build read as the activity list
 * in a column rather than a conversation.
 *
 * A post with no written content of its own — no origin message at all, or
 * an origin message whose text is empty and carries no media (a
 * ROUTINE_MESSAGE with nothing filled in, common on routine-heavy assets) —
 * has nothing to render as a body, so it drops to a single compact line —
 * the same attribution rail as a full row (small initials chip, author,
 * timestamp at the right edge) with the activity title standing in for the
 * body, muted — instead of the full header-plus-body treatment. Quiet
 * status, loud conversation. The activity title is NOT content; it's a
 * system label, so it never earns the full treatment on its own. Such a row
 * can still have replies of its own, so the reply affordance survives into
 * the compact form.
 */
export const AssetChatRow = ({
  item,
  currentAccountId,
  onOpenThread,
  isUnread = false,
  sideChats,
  onOpenSideChat,
  onStartSideChat,
  onCall,
  onCopy,
  onShare,
  testID,
}: AssetChatRowProps) => {
  const { t } = useTranslation();
  const unknownLabel = t('mentions.unknownUser');

  // The header's author is whoever actually wrote the post: the origin
  // message's author when there is one, else the Issue's own creator (the
  // only actor available for the majority case — an activity with no
  // message at all).
  const authorActor = item.originMessage?.createdBy ?? item.createdBy;
  const isOwn = !!currentAccountId && authorActor?.id === currentAccountId;
  const authorName = isOwn
    ? t('chat.row.you')
    : getActorName(authorActor, unknownLabel);
  const timestamp = formatShortDateTimeOrSameDay(
    item.effectiveCreationDate ?? item.createdAt
  );

  const { typedMedia } = useTypedMedia(item.originMessage?.media);

  // "Content" is text or media the person actually supplied — an origin
  // message can exist (a ROUTINE_MESSAGE) and still be content-less, so this
  // is NOT the same check as "no origin message". The activity title is never content on
  // its own; it's a system label.
  const hasContent = !!item.originMessage?.text || typedMedia.length > 0;

  // The whole row opens the thread too (see the outer Pressable below), so
  // the pill's own press must not also bubble into it — nested Pressables
  // fire both handlers on web (RN-Web dispatches onPress via a real DOM
  // click that bubbles, unlike native's responder negotiation), which would
  // be harmless here (same destination either way) but is the wrong pattern
  // to copy for a future pill with a different target.
  const openThreadFromPill = (event?: GestureResponderEvent) => {
    event?.stopPropagation?.();
    onOpenThread?.();
  };

  const replyCount = item.replyCount ?? 0;
  const replyPill =
    replyCount > 0 && onOpenThread ? (
      <Pill
        label={t('chat.row.thread', { count: item.replyCount })}
        showIcon={false}
        raised
        onPress={openThreadFromPill}
        testID={testID ? `${testID}-thread-pill` : undefined}
      />
    ) : null;

  // Deliberately NOT accessibilityRole="button". react-native-web renders a
  // Pressable with that role as a real <button>, and this row contains
  // buttons — the reply and side-chat pills, and the overflow trigger — so
  // the row would be a <button> wrapping <button>s. That is invalid HTML with
  // undefined hit-testing and focus behaviour, and it is what made the
  // trigger flicker while the pointer was on it. The row's press stays as a
  // pointer convenience; the keyboard and screen-reader route into the thread
  // is the overflow menu's Thread entry, which is a real button and is on
  // every row for exactly this reason.

  // Side Chat parents to a MESSAGE, but a row is an ISSUE, so starting one
  // anchors on the origin message. An activity with no message has no id to
  // parent to, so the entry is hidden rather than offering a dead route —
  // never created on demand, which would mutate data because someone opened a
  // menu. Rare on real assets (0 of 400 sampled) but common on test ones.
  const canStartSideChat = !!item.originMessage?.id && !!onStartSideChat;

  const pills = sideChats ?? [];

  // A pending row's id is a placeholder, so none of the menu's destinations
  // resolve yet; the whole menu waits for the server rather than offering
  // routes into an activity that does not exist.
  // A row the composer wrote that the server has not acknowledged yet. Its id
  // is a placeholder, so it cannot be pressed into a thread — the route would
  // resolve to nothing. It goes dim and inert until the real id arrives (see
  // `useCreateChatActivity`).
  const isPending = !!item.isPending;

  // Web reveals the overflow trigger on hover, one row at a time — the same
  // behaviour `issueMessage` has and the reference confirms. Rendering it on
  // every row put a column of orange circles down the feed, competing with the
  // severity glyphs. Native has no hover, and `MessageActionMenu` keeps the
  // trigger present there regardless of this value.
  const [hovered, setHovered] = useState(false);
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onHoverIn: () => setHovered(true),
          onHoverOut: () => setHovered(false),
        }
      : {};

  const actionMenu = (includeCopy: boolean) =>
    isPending || !onOpenThread ? null : (
      <MessageActionMenu
        hovered={hovered}
        messageName={authorName}
        onThread={onOpenThread}
        onSideChat={canStartSideChat ? onStartSideChat : undefined}
        onCall={onCall}
        onCopy={includeCopy ? onCopy : undefined}
        onShare={onShare}
        testID={testID ? `${testID}-menu` : undefined}
      />
    );

  const sideChatPills = pills.length ? (
    <View style={styles.pillRow}>
      {pills.map((pill) => (
        <Pill
          key={pill.parentMessageId}
          label={
            pill.starterName
              ? t('chat.row.sideChatNamed', {
                  count: pill.replyCount,
                  name: pill.starterName,
                })
              : t('chat.row.sideChat', { count: pill.replyCount })
          }
          showIcon={false}
          raised
          onPress={(event?: GestureResponderEvent) => {
            event?.stopPropagation?.();
            onOpenSideChat?.(pill.parentMessageId);
          }}
          testID={
            testID
              ? `${testID}-sidechat-pill-${pill.parentMessageId}`
              : undefined
          }
        />
      ))}
    </View>
  ) : null;

  // A compact line has to stay one line, so several named pills collapse into
  // a single count. Compact is the majority shape on a real asset (13 of 23 on
  // Peterson 1795-9), so this is the common case, not an edge.
  const aggregateSideChatPill = pills.length ? (
    <Pill
      label={t('chat.row.sideChatCount', { count: pills.length })}
      showIcon={false}
      raised
      onPress={(event?: GestureResponderEvent) => {
        event?.stopPropagation?.();
        onOpenSideChat?.(pills[0].parentMessageId);
      }}
      testID={testID ? `${testID}-sidechat-aggregate` : undefined}
    />
  ) : null;

  // No content means no one wrote (or attached) anything — the activity is
  // a status event (ALL_GOOD, an empty routine record), not a post. Giving
  // it the same author-header-plus-body treatment as a real message is what
  // made these crowd out the conversation: render it as a single quiet line
  // instead, same idea as the reference's `kind: 'system'` rows. Attribution
  // survives (composed from existing strings, not a new sentence) because
  // unlike the reference's authorless join/leave events, a person did mark
  // this — dropping who would read as a status with no provenance in an
  // audit-shaped product. Replies are
  // still possible on a content-less row (someone can reply to a routine
  // entry with nothing filled in), so the pill survives into this form too.
  if (!hasContent) {
    return (
      <Pressable
        style={[styles.systemRow, isPending && styles.pending]}
        {...hoverProps}
        testID={testID}
        onPress={onOpenThread}
        disabled={isPending || !onOpenThread}
      >
        <View style={styles.systemTextRow}>
          <Avatar
            type="initial"
            size="extraSmall"
            shape="rounded"
            tone="muted"
            text={getInitials(authorName)}
          />
          <Text style={styles.systemAuthor} numberOfLines={1}>
            {authorName}
          </Text>
          <Text style={styles.systemText} numberOfLines={1}>
            {stripFormattingMarkers(item.title)}
          </Text>
          {isUnread && (
            <Icon name="Dot" size={12} color={colors.semantic.info} />
          )}
          <Text style={styles.systemTimestamp} numberOfLines={1}>
            {timestamp}
          </Text>
          {actionMenu(false)}
        </View>
        {(replyPill || aggregateSideChatPill) && (
          <View style={styles.pillRow}>
            {replyPill}
            {aggregateSideChatPill}
          </View>
        )}
      </Pressable>
    );
  }

  const severityVariant =
    item.type === IssueType.Campaign
      ? 'campaign'
      : severityToVariant[item.severity] || 'attention';

  // Body = the origin message's own text. A message with media but no text
  // (a photo with no caption) renders no text line at all, same as an
  // ordinary media-only chat message.
  //
  // Mentions are stored as `@[name](accountId)`, so the raw text would put a
  // bracket-and-UUID string on screen where the thread view shows `@Name`.
  // `removeId` strips the id, matching the two other plain-text call sites.
  // The mention list is the ISSUE's, not the origin message's, which the
  // feed does not project: the two agree for anything `reportIssue` created
  // (it writes the same array to both), and where they don't the helper
  // falls back to the unknown-user label rather than leaking the raw markup.
  const rawBodyText = item.originMessage?.text;
  const bodyText = rawBodyText?.includes('@[')
    ? replaceMentionPlaceholderWithNames(
        rawBodyText,
        (item.userMentions ?? []) as never,
        unknownLabel,
        true
      )
    : rawBodyText || undefined;

  // Rendered through the same formatter the thread uses, so the markers the
  // composer writes read as bold/italic here instead of as literal `**bold**`.
  // No `onLinkPress`: the whole row is pressable and following a link is the
  // thread's job, so a link segment renders as ordinary formatted text rather
  // than link-coloured text that looks tappable and does nothing.
  const bodyNodes = bodyText
    ? renderFormattedMessageText(bodyText)
    : undefined;

  return (
    <Pressable
      style={[styles.row, isPending && styles.pending]}
      {...hoverProps}
      testID={testID}
      onPress={onOpenThread}
      disabled={isPending || !onOpenThread}
    >
      <View style={styles.header}>
        <Avatar
          type="initial"
          size="medium"
          shape="rounded"
          tone="muted"
          text={getInitials(authorName)}
        />
        <Text style={styles.authorName} numberOfLines={1}>
          {authorName}
        </Text>
        <TypeIcon variant={severityVariant} size={16} />
        {isUnread && <Icon name="Dot" size={12} color={colors.semantic.info} />}
        <Text style={styles.timestamp} numberOfLines={1}>
          {timestamp}
        </Text>
        {/* Media alone earns a full row, but Copy only acts on text. */}
        {actionMenu(!!item.originMessage?.text)}
      </View>

      {bodyNodes &&
        // createElement, not JSX: the formatter's nodes carry no keys (see
        // formattedMessageText) and JSX has no spread-children syntax, so they
        // go in variadically — the same call shape userMessage.tsx uses.
        createElement(
          Text,
          { style: styles.bodyText },
          ...bodyNodes
        )}

      {typedMedia.length > 0 && (
        // Swallows the press rather than letting it reach the row: on web a
        // nested press bubbles as a real DOM click (see the pill above), so
        // opening a photo also navigated to the thread and left the lightbox
        // sitting over it. Wrapping is deliberate — passing `onPress` down to
        // RenderMediaItem instead would suppress the lightbox entirely and
        // swap the inline video player for a static card.
        <Pressable
          style={styles.mediaRow}
          onPress={(event) => event?.stopPropagation?.()}
          testID={testID ? `${testID}-media` : undefined}
        >
          {typedMedia.map((media) => (
            <RenderMediaItem
              key={media.id}
              mediaItem={media}
              displayWidth={120}
            />
          ))}
        </Pressable>
      )}

      {(replyPill || sideChatPills) && (
        <View style={styles.pillRow}>
          {replyPill}
          {sideChatPills}
        </View>
      )}
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  row: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    gap: spacing.sm,
  },
  pending: {
    opacity: 0.6,
  },
  pillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  authorName: {
    ...tokens.typography.label.mediumEmphasised,
    color: role.textStrong,
    flexShrink: 1,
  },
  timestamp: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  bodyText: {
    ...tokens.typography.body.medium,
    color: role.textStrong,
    ...Platform.select({
      web: { whiteSpace: 'pre-wrap' } as never,
      default: {},
    }),
  },
  mediaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  // Same left rail and right-edge timestamp as a full row, so author and
  // time line up down the whole feed instead of every status line finding
  // its own centred width. Only the vertical padding is tighter — that, the
  // 24px avatar and the muted title are what make it read as collapsed.
  systemRow: {
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xs,
  },
  systemTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  systemAuthor: {
    ...tokens.typography.label.mediumEmphasised,
    color: role.textStrong,
    flexShrink: 1,
  },
  systemText: {
    ...tokens.typography.label.medium,
    color: role.textSoft,
    flexShrink: 1,
  },
  systemTimestamp: {
    ...tokens.typography.label.medium,
    color: role.textSoft,
    marginLeft: 'auto',
    paddingLeft: spacing.sm,
    flexShrink: 0,
  },
}));
